<!--
 * @Author: Claude
 * @Description: 欺诈通知数据表格组件
 * @Date: 2023-06-20 14:30:00
 * @Business: 展示欺诈通知列表数据，包括通知ID、通知发送时间、通知接收时间、欺诈类型、源交易流水号等信息，支持点击源交易流水号跳转到交易详情
 * @Interaction: 表格展示欺诈通知数据，支持分页查询，点击交易ID跳转至交易详情页
-->

<template>
  <!-- 使用通用表格组件展示数据 -->
  <custom-table ref="table"
                :table-set="tableCondition"
                :service-function="serviceFunction"
                :first-parameters="getFirstParameters()"
                router-name="/risk/fraud-notification">
  </custom-table>
</template>

<script>
import CustomTable from '@/components/table/custom-table.vue'; // 导入自定义表格组件
import { noDataString } from '@/const/const'; // 导入无数据常量 / Import no data constant
import { isNullForDisplay } from '@/utils/tools/utils'; // 导入空值处理函数 / Import null value handling function

/**
 * @vue-component DataTable
 * @description 欺诈通知数据表格组件，展示通知列表，支持交易详情跳转
 * @description Fraud notification data table component, displaying notification list, supporting transaction details navigation
 */
export default {
  name: 'DataTable', // 组件名称 / Component name

  components: {
    CustomTable, // 注册自定义表格组件 / Register custom table component
  },

  props: {
    /**
     * @prop {Function} serviceFunction
     * @description 获取表格数据的服务函数，由父组件传入
     * @description Service function to get table data, passed from parent component
     * @required
     */
    serviceFunction: {
      type: Function, // 函数类型 / Function type
      required: true, // 必须传入 / Required
    },
  },

  methods: {
    /**
     * @method getFirstParameters
     * @description 生成表格初始查询参数，默认查询最近7天的数据
     * @description Generate initial query parameters for table, default to query last 7 days data
     * @returns {Object} 初始查询参数 / Initial query parameters
     */
    getFirstParameters() {
      // 生成最近7天的日期范围 / Generate date range for last 7 days
      const now = new Date(); // 当前日期 / Current date
      const sevenDaysAgo = new Date(); // 7天前日期 / Date 7 days ago
      sevenDaysAgo.setDate(now.getDate() - 7); // 设置为7天前 / Set to 7 days ago

      // 格式化日期为"YYYY-MM-DD HH:MM:SS"格式 / Format date to "YYYY-MM-DD HH:MM:SS" format
      const startDate = sevenDaysAgo.toISOString().split('T')[0] + ' 00:00:00'; // 开始日期，当天00:00:00 / Start date, 00:00:00 of the day
      const endDate = now.toISOString().split('T')[0] + ' 23:59:59'; // 结束日期，当天23:59:59 / End date, 23:59:59 of the day

      // 返回初始查询参数 / Return initial query parameters
      return {
        startCreateTime: startDate, // 开始时间 / Start time
        endCreateTime: endDate, // 结束时间 / End time
      };
    },
  },

  /**
   * @method data
   * @description 组件数据对象
   * @description Component data object
   * @returns {Object} 组件的响应式数据 / Component reactive data
   */
  data() {
    return {
      /**
       * @property {Array} tableCondition
       * @description 表格列配置数组，每个对象定义表格的一列
       * @description Table column configuration array, each object defines a column
       */
      tableCondition: [
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.notificationId' }, // 列标题：通知ID / Column title: Notification ID
          key: 'serviceId', // 数据字段名 / Data field name
          type: 'text', // 列类型：文本 / Column type: text
        },
        // {
        //   titleBar: { type: 'text', title: 'message.fraudNotification.notifiedTime' }, // 列标题：通知发送时间 / Column title: Notified Time
        //   key: 'reportDate', // 数据字段名 / Data field name
        //   type: 'text', // 列类型：文本 / Column type: text
        // },
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.receivedTime' }, // 列标题：通知接收时间 / Column title: Received Time
          key: 'createTime', // 数据字段名 / Data field name
          type: 'text', // 列类型：文本 / Column type: text
        },
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.fraudType' }, // 列标题：欺诈类型 / Column title: Fraud Type
          key: 'acqFraudType', // 数据字段名 / Data field name
          type: 'text', // 列类型：文本 / Column type: text
          textFunc: (row) => isNullForDisplay(row.acqFraudType, noDataString),
        },
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.transactionId' }, // 列标题：源交易流水号 / Column title: Transaction ID
          key: 'oriTxnId', // 数据字段名 / Data field name
          type: 'click', // 列类型：可点击 / Column type: clickable
          subTableDisplays: [ // 子表显示配置 / Sub-table display configuration
            {
              type: 'text_button', // 按钮类型：文本按钮 / Button type: text button
              key: 'oriTxnId', // 使用的字段名 / Field name to use
              isDisplayFunc: (row) => !!row.oriTxnId, // 只有当交易ID存在时才显示 / Only display when transaction ID exists
              func: (row) => { // 点击事件处理函数 / Click event handler
                // 点击后导航到交易详情页 / Navigate to transaction details page on click
                this.$router.push({
                  path: '/transaction/details',
                  query: { id: row.oriTxnId },
                });
              },
            },
            {
              type: 'text', // 类型：纯文本 / Type: plain text
              key: 'oriTxnId', // 使用的字段名 / Field name to use
              isDisplayFunc: (row) => !row.oriTxnId, // 当交易ID不存在时显示 / Display when transaction ID doesn't exist
            },
          ],
        },
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.transactionAmount' }, // 列标题：源交易金额 / Column title: Transaction Amount
          key: 'oriAmount', // 数据字段名 / Data field name
          type: 'text', // 列类型：文本 / Column type: text
          align: 'right', // 对齐方式：右对齐 / Alignment: right
          textFunc: (row) => {
            // 使用isNullForDisplay函数处理空值 / Use isNullForDisplay function to handle null value
            if (!row.oriAmount && row.oriAmount !== 0) {
              return noDataString; // 无金额时显示无数据标识 / Display no data identifier when no amount
            }
            return `${isNullForDisplay(row.oriCurrency, '')} ${isNullForDisplay(row.oriAmount + '', noDataString)}`; // 显示币种和金额 / Display currency and amount
          },
        },
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.paymentMethod' }, // 列标题：支付方式 / Column title: Payment Method
          key: 'cardBrand', // 数据字段名 / Data field name
          type: 'text', // 列类型：文本 / Column type: text
          textFunc: (row) => isNullForDisplay(row.cardBrand, noDataString), // 使用isNullForDisplay处理空值 / Use isNullForDisplay to handle null value
        },
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.disputePresence' }, // 列标题：源交易拒付状态 / Column title: Dispute Presence
          key: 'chargebackStatusLabel', // 数据字段名 / Data field name
          type: 'text', // 列类型：文本 / Column type: text
          textFunc: (row) => isNullForDisplay(row.chargebackStatusLabel, noDataString), // 使用isNullForDisplay处理空值 / Use isNullForDisplay to handle null value
        },
        {
          titleBar: { type: 'text', title: 'message.fraudNotification.chargebackId' }, // 列标题：拒付流水号 / Column title: Chargeback ID
          key: 'chargebackId', // 数据字段名 / Data field name
          type: 'text', // 列类型：文本 / Column type: text
          textFunc: (row) => isNullForDisplay(row.chargebackId, noDataString), // 使用isNullForDisplay处理空值 / Use isNullForDisplay to handle null value
        },
        {
          key: 'buttons',
          titleBar: { title: 'message.tableCommon.operation', type: 'operations' },
          type: 'operations',
          subTableDisplays: [
          ],
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
// 表格组件特定样式
// Table component specific styles
</style>
